import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Calendar, Users, Send, Loader2, PauseCircle, PlayCircle, Trash2, ChevronDown, ChevronUp, CheckCircle, XCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

function ScheduleMessage() {
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [contactMode, setContactMode] = useState('existing'); // 'existing' or 'manual'
  const [selectedContactId, setSelectedContactId] = useState('');
  const [manualPhone, setManualPhone] = useState('');

  const [messageContent, setMessageContent] = useState('');
  const [recurrenceType, setRecurrenceType] = useState('once');
  const [scheduledAt, setScheduledAt] = useState('');
  const [dailyTime, setDailyTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [creating, setCreating] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [logsData, setLogsData] = useState({});

  // ✅ State for API setup modal
  const [showApiSetupModal, setShowApiSetupModal] = useState(false);

  useEffect(() => {
    loadContacts();
    loadSchedules();
  }, []);

  const loadContacts = async () => {
    try {
      const res = await API.get('/contacts');
      setContacts(res.data.data || []);
    } catch (error) {
      console.error('Load contacts error:', error);
    }
  };

  const loadSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const res = await API.get('/scheduled-messages');
      setSchedules(res.data.data || []);
    } catch (error) {
      console.error('Load schedules error:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const resetForm = () => {
    setSelectedContactId('');
    setManualPhone('');
    setMessageContent('');
    setRecurrenceType('once');
    setScheduledAt('');
    setDailyTime('');
    setStartDate('');
    setEndDate('');
  };

  const handleCreate = async () => {
    const phone = contactMode === 'existing'
      ? contacts.find(c => String(c.id) === String(selectedContactId))?.phone
      : manualPhone;

    if (!phone) {
      toast.error('Please select or enter a phone number');
      return;
    }
    if (!messageContent.trim()) {
      toast.error('Message content is required');
      return;
    }
    if (recurrenceType === 'once' && !scheduledAt) {
      toast.error('Please pick a date & time');
      return;
    }
    if (recurrenceType === 'daily' && !dailyTime) {
      toast.error('Please pick a daily time');
      return;
    }

    const toastId = toast.loading('Scheduling message...');
    setCreating(true);
    try {
      const body = {
        phone,
        contactId: contactMode === 'existing' ? Number(selectedContactId) : undefined,
        messageContent,
        recurrenceType,
        scheduledAt: recurrenceType === 'once' ? scheduledAt.replace('T', ' ') + ':00' : undefined,
        dailyTime: recurrenceType === 'daily' ? dailyTime + ':00' : undefined,
        startDate: recurrenceType === 'daily' && startDate ? startDate : undefined,
        endDate: recurrenceType === 'daily' && endDate ? endDate : undefined,
      };

      await API.post('/scheduled-messages', body);
      toast.success('Message scheduled!', { id: toastId });
      resetForm();
      loadSchedules();
    } catch (error) {
      // ✅ Intercept unconfigured WhatsApp error
      if (error.response?.data?.errorCode === 'WHATSAPP_NOT_CONFIGURED') {
        toast.dismiss(toastId);
        setShowApiSetupModal(true);
      } else {
        toast.error(error.response?.data?.message || 'Failed to schedule message', { id: toastId });
      }
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    const toastId = toast.loading(newStatus === 'paused' ? 'Pausing...' : 'Resuming...');
    try {
      await API.patch(`/scheduled-messages/${id}/toggle`, { status: newStatus });
      toast.success(`Schedule ${newStatus}`, { id: toastId });
      loadSchedules();
    } catch (error) {
      toast.error('Failed to update schedule', { id: toastId });
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled message?')) return;
    const toastId = toast.loading('Cancelling...');
    try {
      await API.delete(`/scheduled-messages/${id}`);
      toast.success('Schedule cancelled', { id: toastId });
      loadSchedules();
    } catch (error) {
      toast.error('Failed to cancel', { id: toastId });
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!logsData[id]) {
      try {
        const res = await API.get(`/scheduled-messages/${id}`);
        setLogsData(prev => ({ ...prev, [id]: res.data.data.logs }));
      } catch (error) {
        toast.error('Failed to load history');
      }
    }
  };

  const statusBadge = (status) => {
    const map = {
      active: { bg: '#dcfce7', color: '#166534', label: 'Active' },
      paused: { bg: '#fef3c7', color: '#92400e', label: 'Paused' },
      completed: { bg: '#dbeafe', color: '#1e40af', label: 'Completed' },
      cancelled: { bg: '#f1f5f9', color: '#64748b', label: 'Cancelled' },
      failed: { bg: '#fee2e2', color: '#991b1b', label: 'Failed' },
    };
    const s = map[status] || map.active;
    return (
      <span style={{
        padding: '3px 10px', borderRadius: '20px', fontSize: '12px',
        fontWeight: '700', background: s.bg, color: s.color
      }}>{s.label}</span>
    );
  };

  const logStatusBadge = (status) => {
    const map = {
      sent: { bg: '#dbeafe', color: '#1e40af', icon: <CheckCircle size={11} /> },
      delivered: { bg: '#dcfce7', color: '#166534', icon: <CheckCircle size={11} /> },
      read: { bg: '#ede9fe', color: '#6d28d9', icon: <CheckCircle size={11} /> },
      failed: { bg: '#fee2e2', color: '#991b1b', icon: <XCircle size={11} /> },
    };
    const s = map[status] || map.sent;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '2px 8px', borderRadius: '20px', fontSize: '11px',
        fontWeight: '700', background: s.bg, color: s.color
      }}>{s.icon} {status}</span>
    );
  };

  return (
    <div className="dash-page">
      <div className="dash-header">
        <div>
          <h1 className="dash-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Clock size={26} color="#1e3a5f" /> Schedule Message
          </h1>
          <p className="dash-subtitle">Schedule a one-time or daily recurring WhatsApp message</p>
        </div>
      </div>

      {/* Create Schedule Form */}
      <div className="dash-card mb-lg">
        <h2 className="settings-tab-title" style={{ marginBottom: '16px' }}>New Schedule</h2>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <button
            onClick={() => setContactMode('existing')}
            className={contactMode === 'existing' ? 'settings-btn-save' : 'settings-btn-test'}
            type="button"
          >
            <Users size={15} /> Existing Contact
          </button>
          <button
            onClick={() => setContactMode('manual')}
            className={contactMode === 'manual' ? 'settings-btn-save' : 'settings-btn-test'}
            type="button"
          >
            ✍️ Enter Number
          </button>
        </div>

        {contactMode === 'existing' ? (
          <div className="settings-form-group">
            <label className="settings-form-label">Select Contact</label>
            <select className="settings-form-input" value={selectedContactId} onChange={(e) => setSelectedContactId(e.target.value)}>
              <option value="">— Choose a contact —</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="settings-form-group">
            <label className="settings-form-label">Phone Number</label>
            <input
              type="text"
              className="settings-form-input"
              placeholder="+919876543210"
              value={manualPhone}
              onChange={(e) => setManualPhone(e.target.value)}
            />
            <p className="settings-form-hint">Enter 10-digit number or with +91.</p>
          </div>
        )}

        <div className="settings-form-group">
          <label className="settings-form-label">Message <span className="settings-form-required">*</span></label>
          <textarea
            className="settings-form-textarea"
            rows={3}
            placeholder="Type your message..."
            value={messageContent}
            onChange={(e) => setMessageContent(e.target.value)}
          />
        </div>

        <div className="settings-form-group">
          <label className="settings-form-label">Schedule Type</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setRecurrenceType('once')}
              className={recurrenceType === 'once' ? 'settings-btn-save' : 'settings-btn-test'}
              type="button"
            >
              <Calendar size={15} /> Once
            </button>
            <button
              onClick={() => setRecurrenceType('daily')}
              className={recurrenceType === 'daily' ? 'settings-btn-save' : 'settings-btn-test'}
              type="button"
            >
              <Clock size={15} /> Daily
            </button>
          </div>
        </div>

        {recurrenceType === 'once' ? (
          <div className="settings-form-group">
            <label className="settings-form-label">Date & Time <span className="settings-form-required">*</span></label>
            <input
              type="datetime-local"
              className="settings-form-input"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          </div>
        ) : (
          <>
            <div className="settings-form-group">
              <label className="settings-form-label">Time (every day) <span className="settings-form-required">*</span></label>
              <input
                type="time"
                className="settings-form-input"
                value={dailyTime}
                onChange={(e) => setDailyTime(e.target.value)}
              />
            </div>
            <div className="setup-request-grid-2">
              <div className="settings-form-group">
                <label className="settings-form-label">Start Date (optional)</label>
                <input type="date" className="settings-form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="settings-form-group">
                <label className="settings-form-label">End Date (optional, leave blank for forever)</label>
                <input type="date" className="settings-form-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          </>
        )}

        <div className="settings-actions">
          <button onClick={handleCreate} disabled={creating} className="settings-btn-save">
            {creating ? <><Loader2 size={15} /> Scheduling...</> : <><Send size={15} /> Schedule Message</>}
          </button>
        </div>
      </div>

      {/* Schedules List */}
      <div className="dash-card">
        <h2 className="settings-tab-title" style={{ marginBottom: '16px' }}>Your Scheduled Messages</h2>

        {loadingSchedules ? (
          <div className="loading-container" style={{ padding: '30px 0' }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#1e3a5f' }} />
          </div>
        ) : schedules.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Clock size={56} color="#cbd5e1" /></div>
            <h3 className="empty-state-title">No Scheduled Messages</h3>
            <p className="empty-state-description">Messages you schedule will appear here.</p>
          </div>
        ) : (
          <div className="table-container table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Contact / Phone</th>
                  <th>Message</th>
                  <th>Type</th>
                  <th>Next Run</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map(s => (
                  <>
                    <tr key={s.id}>
                      <td>{s.contact_name || s.phone}</td>
                      <td style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.message_content}
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{s.recurrence_type}</td>
                      <td>
                        {s.next_run_at
                          ? new Date(s.next_run_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                          : '—'}
                      </td>
                      <td>{statusBadge(s.status)}</td>
                      <td className="text-center">
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {(s.status === 'active' || s.status === 'paused') && (
                            <button onClick={() => handleToggle(s.id, s.status)} className="btn btn-info btn-sm" title={s.status === 'active' ? 'Pause' : 'Resume'}>
                              {s.status === 'active' ? <PauseCircle size={13} /> : <PlayCircle size={13} />}
                            </button>
                          )}
                          {s.status !== 'cancelled' && s.status !== 'completed' && (
                            <button onClick={() => handleCancel(s.id)} className="btn btn-info btn-sm" style={{ background: '#ef4444' }} title="Cancel">
                              <Trash2 size={13} />
                            </button>
                          )}
                          <button onClick={() => toggleExpand(s.id)} className="btn btn-info btn-sm" title="View History">
                            {expandedId === s.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === s.id && (
                      <tr>
                        <td colSpan={6} style={{ background: '#f8fafc', padding: '14px 20px' }}>
                          <strong style={{ fontSize: '13px', color: '#475569' }}>Send History</strong>
                          {!logsData[s.id] ? (
                            <div style={{ padding: '10px 0' }}><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /></div>
                          ) : logsData[s.id].length === 0 ? (
                            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '8px 0 0' }}>No sends yet.</p>
                          ) : (
                            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {logsData[s.id].map(log => (
                                <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px', color: '#475569' }}>
                                  {logStatusBadge(log.status)}
                                  <span>{new Date(log.sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                  {log.error_message && <span style={{ color: '#dc2626' }}>— {log.error_message}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ✅ PROFESSIONAL API SETUP REQUIRED POP-UP MODAL */}
      {showApiSetupModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            maxWidth: '500px',
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e2e8f0',
            animation: 'modalSlideIn 0.25s ease-out'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1e3a5f, #0f243e)',
              padding: '26px 28px',
              color: 'white',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowApiSetupModal(false)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>

              <div style={{
                width: '46px',
                height: '46px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '14px'
              }}>
                <AlertTriangle size={24} color="#fcd34d" />
              </div>

              <h2 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '700' }}>
                WhatsApp API Setup Required
              </h2>
              <p style={{ margin: 0, fontSize: '13px', opacity: 0.85, lineHeight: '1.5' }}>
                Active Meta WhatsApp Business credentials are required to schedule automated messages.
              </p>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px 28px' }}>
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '22px'
              }}>
                <p style={{ margin: '0 0 8px', fontWeight: '700', fontSize: '13.5px', color: '#1e293b' }}>
                  Why is this needed?
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: '1.6' }}>
                  Scheduled messages are dispatched automatically through Meta Cloud API. To deliver these messages to your contacts on time, you must connect your official WhatsApp Business credentials first.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowApiSetupModal(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Dismiss
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowApiSetupModal(false);
                    navigate('/settings');
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    background: '#1e3a5f',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '13.5px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(30, 58, 95, 0.2)'
                  }}
                >
                  Configure WhatsApp API <ArrowRight size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScheduleMessage;